import { FC, useEffect } from 'react';
import { IonButton, IonContent } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { Modal } from '../components/Modal';
import { ActiveChatView } from '../helper-bee/ActiveChatView';
import { ConversationListView } from '../helper-bee/ConversationListView';
import { CareMapState } from '../caremap/CareMapState';
import { CareMapOnScreen } from '../caremap/CareMapOnScreen';
import { OrganizationsState } from '../organizations/OrganizationsState';
import { AppHeaderOverrideState } from '../AppHeaderOverrideState';
import { HEADER_Z_INDEX } from '../AppHeader';
import { useBreakpoints } from '../theme/useBreakpoints';
import { ChevronLeftIcon, CloseIcon, MinusIcon } from '../theme/Icons';
import { AgencyBeaState } from './AgencyBeaState';

const PANEL_WIDTH = 440;

const useStyles = createUseStyles({
    // Docked at the window's right edge, under the header, so it stays open while the person moves
    // between clients and the active client follows the page.
    dock: {
        position: 'fixed',
        top: ({ headerHeight }: { headerHeight: number }) => headerHeight,
        right: 0,
        bottom: 0,
        width: PANEL_WIDTH,
        maxWidth: '100vw',
        display: 'flex',
        flexDirection: 'column',
        background: 'white',
        borderLeft: '1px solid var(--ion-color-step-150)',
        boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.08)',
        zIndex: HEADER_Z_INDEX - 1
    },
    dockHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '6px 8px',
        borderBottom: '1px solid var(--ion-color-step-100)',
        background: 'var(--ion-color-secondary)',
        color: 'white'
    },
    dockTitle: {
        flex: 1,
        fontWeight: 600,
        fontSize: 15,
        paddingLeft: 4
    },
    dockBody: {
        flex: 1,
        minHeight: 0
    }
});

const useChipStyles = createUseStyles({
    chip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        marginTop: 4,
        padding: '2px 4px 2px 10px',
        borderRadius: 12,
        fontSize: 13,
        background: 'var(--ion-color-step-100)',
        color: 'var(--ion-color-dark)'
    },
    chipClear: {
        '--padding-start': '4px',
        '--padding-end': '4px',
        height: 22,
        margin: 0
    }
});

const SUGGESTIONS_FOR_CLIENT = [
    'Summarize the last 30 days',
    'What is outstanding for this client?',
    'When is the next appointment?'
];

const CASELOAD_STARTERS = [
    'What should staff know before seeing clients tomorrow?',
    'Who had a hospital or ER visit this month?',
    'Who hasn\'t had a family entry in two weeks?'
];

/** What the chat is asking about: the client on screen, cleared to ask across the caseload instead. */
const ActiveClientChip: FC = () => {
    const classes = useChipStyles();
    const agencyBea = useInstance(AgencyBeaState);
    useSubscription(() => agencyBea);
    const { activeClient } = agencyBea.state;

    if (!activeClient) {
        return <span className={classes.chip} data-id="agency-bea-scope">Asking across all clients</span>;
    }
    return (
        <span className={classes.chip} data-id="agency-bea-scope">
            Asking about: <span className="mp-mask">{activeClient.name}</span>
            <IonButton fill="clear" size="small" color="medium" className={classes.chipClear} data-id="agency-bea-clear-client" onClick={agencyBea.clearActiveClient} aria-label="Ask across all clients instead">
                <CloseIcon color="medium" size="small" />
            </IonButton>
        </span>
    );
};

const PanelContent: FC = () => {
    const agencyBea = useInstance(AgencyBeaState);
    useSubscription(() => agencyBea);
    const { currentView, activeClient } = agencyBea.state;

    if (currentView === 'list') {
        return <ConversationListView chatState={agencyBea} data-id="agency-bea-conversation-list" />;
    }
    return (
        <ActiveChatView
            chatState={agencyBea}
            data-id="agency-bea-chat-view"
            headerDetail={<ActiveClientChip />}
            suggestions={activeClient ? SUGGESTIONS_FOR_CLIENT : CASELOAD_STARTERS}
        />
    );
};

/** The wide-screen shell: a panel docked beside the page, under whatever header the page has. */
const AgencyBeaDock: FC<{ title: string; onBack?: () => void }> = ({ title, onBack }) => {
    const header = useInstance(AppHeaderOverrideState);
    useSubscription(() => header);
    const classes = useStyles({ headerHeight: header.height });
    const agencyBea = useInstance(AgencyBeaState);

    return (
        <div className={classes.dock} data-id="agency-bea-panel" data-type="agency-bea-dock">
            <div className={classes.dockHeader}>
                {onBack && (
                    <IonButton fill="clear" color="light" size="small" onClick={onBack} data-id="agency-bea-back" aria-label="Back to conversations">
                        <ChevronLeftIcon color="light" />
                    </IonButton>
                )}
                <span className={classes.dockTitle}>{title}</span>
                <IonButton fill="clear" color="light" size="small" onClick={agencyBea.minimize} data-id="agency-bea-minimize" aria-label="Minimize">
                    <MinusIcon color="light" />
                </IonButton>
                <IonButton fill="clear" color="light" size="small" onClick={agencyBea.close} data-id="agency-bea-close" aria-label="Close">
                    <CloseIcon color="light" />
                </IonButton>
            </div>
            <div className={classes.dockBody}>
                <PanelContent />
            </div>
        </div>
    );
};

/**
 * Agency Bea's surface: a panel docked beside the page on a wide window, a full-screen modal on a
 * narrower one, whatever the device. One shell either way, over one conversation state.
 */
export const AgencyBeaPanel: FC = () => {
    const agencyBea = useInstance(AgencyBeaState);
    useSubscription(() => agencyBea);
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);
    const onScreen = useInstance(CareMapOnScreen);
    useSubscription(() => onScreen);
    const orgState = useInstance(OrganizationsState);
    useSubscription(() => orgState);
    const breakpoints = useBreakpoints();

    const { isOpen, currentView, organization } = agencyBea.state;
    const activeCareMapId = careMapState.getActiveCaremap()?.careMapId;
    const careMapIdOnScreen = onScreen.getCareMapId();
    // The roster can arrive after a deep link lands on a client's page.
    const rosterSize = organization ? orgState.getOrganizationCareMaps(organization.orgId).length : 0;

    // The chip follows the client on screen as the person moves between clients and other pages.
    useEffect(() => {
        if (isOpen) {
            agencyBea.syncActiveClient();
        }
    }, [isOpen, activeCareMapId, careMapIdOnScreen, rosterSize, agencyBea]);

    const title = organization ? `Bea for ${organization.name}` : 'Bea';
    const onBack = currentView === 'chat' ? agencyBea.showConversationList : undefined;
    // By width alone, at the width where the bottom tab bar goes: below it the bar would sit over the dock's composer.
    const isWide = breakpoints.isAboveBreakpoint('lg');

    // The modal covers the page; the dock sits beside it.
    useEffect(() => {
        agencyBea.setCoversPage(!isWide);
    }, [isWide, agencyBea]);

    // The modal stays mounted so that widening the window while it is open dismisses it properly:
    // unmounting an open one would leave the page behind it hidden from screen readers.
    return (
        <>
            <Modal
                isOpen={isOpen && !isWide}
                titleColor="secondary"
                buttonColor="light"
                title={title}
                data-id="agency-bea-panel"
                closeHandler={agencyBea.close}
                minimizeHandler={agencyBea.minimize}
                backHandler={onBack}
                fullScreen
            >
                <IonContent scrollY={false}>
                    <PanelContent />
                </IonContent>
            </Modal>
            {isOpen && isWide && <AgencyBeaDock title={title} onBack={onBack} />}
        </>
    );
};
